/**
 * The firm's own addresses (#420), as the console reaches them: Settings → Адреси, the
 * desk's Заключи…, the count on the sidebar and the notice at sign-in — and Reports → Адреси
 * за заключване, which joins a row's old bookings and addresses to a place (#434). A join is
 * Geo's ledger, written by the host into each module's own rows, so it lives here with the
 * places rather than with the reports, which never write.
 */

import { ApiError, download, LONG_TIMEOUT_MS, request, type Schema } from '@taxi/api-client';

export type PlaceView = Schema<'PlaceView'>;
export type PlaceList = Schema<'PlaceList'>;
export type PlaceHistory = Schema<'PlaceHistory'>;
export type PlaceHistoryEntry = Schema<'PlaceHistoryEntry'>;
export type PlaceReview = Schema<'PlaceReview'>;
export type PlaceReviewList = Schema<'PlaceReviewList'>;
export type PlaceSnapshot = Schema<'PlaceSnapshot'>;
export type PlaceSettings = Schema<'PlaceSettings'>;
export type PlaceImportResult = Schema<'PlaceImportResult'>;
export type FirmPlaceHit = Schema<'FirmPlaceHit'>;
export type SimilarPlace = Schema<'SimilarPlace'>;
export type SimilarPlaces = Schema<'SimilarPlaces'>;
export type PlaceJoinRequest = Schema<'PlaceJoinRequest'>;
export type PlaceJoinResult = Schema<'PlaceJoinResult'>;
export type PlaceJoinUndone = Schema<'PlaceJoinUndone'>;
export type PlaceJoinList = Schema<'PlaceJoinList'>;
export type PlaceJoinEntry = Schema<'PlaceJoinEntry'>;

/** Why a place was offered for a row, as the server names it. */
export type SimilarKind = 'JoinedBefore' | 'Same' | 'Building' | 'Words' | 'Name' | 'Near' | 'Search';

/** What a row the places are asked about is: its building and entrance, its point, and any words typed. */
export interface SimilarQuery {
  building: string;
  entrance?: string | null;
  longitude?: number | null;
  latitude?: number | null;
  q?: string;
}

/** What the Settings page and the desk send for a place. */
export interface PlaceDraft {
  building: string;
  entrance: string | null;
  longitude: number;
  latitude: number;
  stopLongitude?: number | null;
  stopLatitude?: number | null;
  note?: string | null;
  names?: string[];

  /** Google's name and where it was booked, when locked from one of Google's (#478). */
  google?: Schema<'GoogleNamed'> | null;
}

export const placesApi = {
  list: () => request<PlaceList>('/api/places'),

  add: (draft: PlaceDraft) => request<PlaceView>('/api/places', { method: 'POST', body: draft }),

  update: (id: string, draft: PlaceDraft) =>
    request<PlaceView>(`/api/places/${id}`, { method: 'PUT', body: draft }),

  remove: (id: string) => request<void>(`/api/places/${id}`, { method: 'DELETE' }),

  history: (id: string) => request<PlaceHistory>(`/api/places/${id}/history`),

  restore: (id: string, changeId: string) =>
    request<PlaceView>(`/api/places/${id}/restore/${changeId}`, { method: 'POST' }),

  review: () => request<PlaceReviewList>('/api/places/review'),

  waiting: () => request<{ waiting: number }>('/api/places/review/count'),

  approve: (id: string) => request<void>(`/api/places/${id}/approve`, { method: 'POST' }),

  reject: (id: string) => request<void>(`/api/places/${id}/reject`, { method: 'POST' }),

  approveReport: (changeId: string) =>
    request<void>(`/api/places/reports/${changeId}/approve`, { method: 'POST' }),

  rejectReport: (changeId: string) =>
    request<void>(`/api/places/reports/${changeId}/reject`, { method: 'POST' }),

  /** The places a row could be, best first: by why, then how far — then what the words find. */
  similar: (asked: SimilarQuery) => {
    const query = new URLSearchParams({ building: asked.building });

    if (asked.entrance != null) query.set('entrance', asked.entrance);
    if (asked.longitude != null && asked.latitude != null) {
      query.set('lon', String(asked.longitude));
      query.set('lat', String(asked.latitude));
    }
    if (asked.q != null && asked.q.trim() !== '') query.set('q', asked.q.trim());

    return request<SimilarPlaces>(`/api/places/similar?${query.toString()}`);
  },

  /** Joins a row's bookings and addresses to a place. The join's id is the dialog's: sent twice, it joins once. */
  join: (placeId: string, join: PlaceJoinRequest) =>
    request<PlaceJoinResult>(`/api/places/${placeId}/joins`, { method: 'POST', body: join, timeoutMs: LONG_TIMEOUT_MS }),

  /** Finishes a join that was cut off, from the ids it was given. */
  finishJoin: (placeId: string, joinId: string) =>
    request<PlaceJoinResult>(`/api/places/${placeId}/joins/${joinId}/finish`, { method: 'POST', timeoutMs: LONG_TIMEOUT_MS }),

  /** Undoes a join. Asked again, it changes nothing. */
  undoJoin: (placeId: string, joinId: string) =>
    request<PlaceJoinUndone>(`/api/places/${placeId}/joins/${joinId}/undo`, { method: 'POST' }),

  /** A place's joins, newest first, for its history. */
  joins: (placeId: string) => request<PlaceJoinList>(`/api/places/${placeId}/joins`),

  settings: () => request<PlaceSettings>('/api/tenant/settings/places'),

  saveSettings: (settings: PlaceSettings) =>
    request<PlaceSettings>('/api/tenant/settings/places', { method: 'PUT', body: settings }),

  /** The file's text, read by the browser; nothing is saved unless `commit`. */
  /** `joinSimilar`: an address reading the same as one already here — Бряст, завод Бряст — is that one. */
  import: (csv: string, commit: boolean, joinSimilar = false) =>
    request<PlaceImportResult>('/api/places/import', {
      method: 'POST',
      body: { csv, commit, joinSimilar },
      timeoutMs: LONG_TIMEOUT_MS,
    }),

  /**
   * Every place as a CSV, handed to the browser to save. Through the authenticated client,
   * as every file the console downloads is: a plain link carries no token.
   */
  async export(): Promise<void> {
    const { blob, filename } = await download('/api/places/export');
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename ?? 'addresses.csv';
    link.click();

    URL.revokeObjectURL(url);
  },
};

/** The draft a place already is, for an edit that changes one thing. */
export function draftOf(place: PlaceView): PlaceDraft {
  return {
    building: place.building,
    entrance: place.entrance ?? null,
    longitude: place.door.longitude,
    latitude: place.door.latitude,
    stopLongitude: place.stop?.longitude ?? null,
    stopLatitude: place.stop?.latitude ?? null,
    note: place.note ?? null,
    names: [...place.names],
  };
}

/**
 * The place a refused add or move reads the same as, so the screen can offer that one instead
 * of a dead end: one per address and entrance, and the refusal names which (#420).
 */
export function duplicateOf(failure: unknown): string | null {
  return failure instanceof ApiError && failure.problem.code === 'place.duplicate'
    ? (failure.problem.arguments?.id ?? null)
    : null;
}

/**
 * One word passed between screens, as the setup's is: the places just changed, so the count on
 * the sidebar and the notice at sign-in ask the server again at once rather than at their next
 * minute. The server is the authority on the number either way.
 */
const target = new EventTarget();

const CHANGED = 'taxi:places-changed';

export function notifyPlacesChanged(): void {
  target.dispatchEvent(new Event(CHANGED));
}

/** Runs the callback on every change until the returned function is called. */
export function onPlacesChanged(callback: () => void): () => void {
  target.addEventListener(CHANGED, callback);

  return () => target.removeEventListener(CHANGED, callback);
}
